/**
 * Wishlist & Compare
 * Stores product data in localStorage. No third-party app required.
 */
(function () {
  'use strict';

  var WC = (window.WishlistCompare = {});

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function storageGet(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (e) {
      return [];
    }
  }

  function storageSet(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  // ─── Wishlist ────────────────────────────────────────────────────────────────

  WC.Wishlist = {
    key: 'wc_wishlist',

    get: function () {
      return storageGet(this.key);
    },

    _save: function (items) {
      storageSet(this.key, items);
      this._updateCounts();
      this._updateButtons();
    },

    add: function (product) {
      var items = this.get();
      if (!items.some(function (p) { return p.id === product.id; })) {
        items.push(product);
        this._save(items);
      }
    },

    remove: function (id) {
      this._save(this.get().filter(function (p) { return p.id !== id; }));
    },

    toggle: function (product) {
      if (this.contains(product.id)) {
        this.remove(product.id);
        return false;
      }
      this.add(product);
      return true;
    },

    contains: function (id) {
      return this.get().some(function (p) { return p.id === id; });
    },

    clear: function () {
      localStorage.removeItem(this.key);
      this._updateCounts();
      this._updateButtons();
    },

    _updateCounts: function () {
      var count = this.get().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    _updateButtons: function () {
      var self = this;
      document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
        var id = parseInt(btn.dataset.productId, 10);
        if (self.contains(id)) {
          btn.classList.add('wc-active');
          btn.setAttribute('aria-label', 'Remove from wishlist');
          btn.title = 'Remove from wishlist';
        } else {
          btn.classList.remove('wc-active');
          btn.setAttribute('aria-label', 'Add to wishlist');
          btn.title = 'Add to wishlist';
        }
      });
    },
  };

  // ─── Compare ────────────────────────────────────────────────────────────────

  WC.Compare = {
    key: 'wc_compare',
    max: 4,

    get: function () {
      return storageGet(this.key);
    },

    _save: function (items) {
      storageSet(this.key, items);
      this._updateBar();
      this._updateButtons();
    },

    add: function (product) {
      var items = this.get();
      if (items.length >= this.max) {
        alert('You can compare up to ' + this.max + ' products at a time.');
        return false;
      }
      if (!items.some(function (p) { return p.id === product.id; })) {
        items.push(product);
        this._save(items);
      }
      return true;
    },

    remove: function (id) {
      this._save(this.get().filter(function (p) { return p.id !== id; }));
    },

    toggle: function (product) {
      if (this.contains(product.id)) {
        this.remove(product.id);
        return false;
      }
      return this.add(product);
    },

    contains: function (id) {
      return this.get().some(function (p) { return p.id === id; });
    },

    clear: function () {
      localStorage.removeItem(this.key);
      this._updateBar();
      this._updateButtons();
    },

    _updateBar: function () {
      var items = this.get();
      var bar = document.getElementById('wc-compare-bar');
      if (!bar) return;

      bar.classList.toggle('wc-compare-bar--visible', items.length >= 2);

      var preview = bar.querySelector('[data-compare-preview]');
      if (preview) {
        preview.innerHTML = items
          .map(function (p) {
            return (
              '<span class="wc-compare-bar__chip">' +
              '<img src="' + p.image + '" alt="" width="32" height="32">' +
              p.title +
              '<button class="wc-compare-bar__remove" data-remove-compare="' +
              p.id +
              '" aria-label="Remove">&#x2715;</button></span>'
            );
          })
          .join('');
      }

      var countEl = bar.querySelector('[data-compare-count]');
      if (countEl) countEl.textContent = items.length;
    },

    _updateButtons: function () {
      var self = this;
      document.querySelectorAll('[data-compare-btn]').forEach(function (btn) {
        var id = parseInt(btn.dataset.productId, 10);
        var label = btn.querySelector('.wc-compare-btn-text');
        if (self.contains(id)) {
          btn.classList.add('wc-active');
          if (label) label.textContent = 'Remove';
        } else {
          btn.classList.remove('wc-active');
          if (label) label.textContent = 'Compare';
        }
      });
    },
  };

  // ─── Page Renderers ──────────────────────────────────────────────────────────

  WC.renderWishlistPage = function (container) {
    var items = WC.Wishlist.get();

    if (items.length === 0) {
      container.innerHTML =
        '<p class="wc-empty-msg">Your wishlist is empty. ' +
        '<a href="/collections/all">Browse products</a></p>';
      return;
    }

    var html = '<div class="grid grid-uniform">';
    items.forEach(function (p) {
      var hasVariants = p.variants && p.variants.length > 1;
      var hasOptions = p.type_options && p.type_options.length > 0;

      // Find the stored variant in the variants array (works for single or multi)
      var storedVariant = null;
      if (p.variants) {
        storedVariant = p.variants.filter(function (v) { return v.id === p.variant_id; })[0] || null;
      }

      // Availability flags
      var allSoldOut = hasVariants && p.variants.every(function (v) { return !v.available; });
      var singleSoldOut = !hasVariants && storedVariant && !storedVariant.available;
      var isSoldOut = allSoldOut || singleSoldOut;

      // Display price: use stored variant's price if known
      var displayPrice = storedVariant ? storedVariant.price : p.price;

      // Variant dropdown (yarn bases) — omit entirely if all sold out
      var variantSelect = '';
      if (hasVariants && !allSoldOut) {
        variantSelect = '<select class="wc-variant-select" aria-label="Choose a base"><option value="">— Choose a base —</option>';
        p.variants.forEach(function (v) {
          // Only pre-select the stored variant if it is actually available
          var isSel = (v.id === p.variant_id && v.available) ? ' selected' : '';
          var soldOut = v.available ? '' : ' — Sold out';
          variantSelect +=
            '<option value="' + v.id + '" data-price="' + v.price.replace(/"/g, '&quot;') + '"' +
            (v.available ? '' : ' disabled') + isSel + '>' +
            v.title + soldOut + '</option>';
        });
        variantSelect += '</select>';
      }

      // Type options dropdown (findings etc.)
      var optSelect = '';
      if (hasOptions) {
        optSelect = '<select class="wc-type-select" aria-label="Choose an option"><option value="">Choose an option…</option>';
        p.type_options.forEach(function (o) {
          optSelect += '<option value="' + o.replace(/"/g, '&quot;') + '">' + o + '</option>';
        });
        optSelect += '</select>';
      }

      // ATC button: pre-populate variant only when it is in stock
      var atcVariantId = '';
      if (!isSoldOut) {
        if (hasVariants) {
          atcVariantId = (storedVariant && storedVariant.available) ? (p.variant_id || '') : '';
        } else {
          atcVariantId = p.variant_id || '';
        }
      }
      var cartBtn = isSoldOut
        ? '<button class="wc-atc-btn" disabled>Sold Out</button>'
        : '<button class="wc-atc-btn" data-atc-variant="' + atcVariantId + '">Add to Cart</button>';

      html +=
        '<div class="grid__item grid-product large--one-third medium--one-half">' +
        '<div class="grid-product__wrapper">' +
        '<div class="grid-product__image-wrapper">' +
        '<a href="' + p.url + '" class="grid-product__image-link">' +
        '<img class="grid-product__image" src="' + p.image + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy">' +
        '</a>' +
        '</div>' +
        '<a href="' + p.url + '" class="grid-product__meta">' +
        '<span class="grid-product__title">' + p.title + '</span>' +
        '<span class="grid-product__price-wrap"><span class="long-dash">—</span>' +
        '<span class="grid-product__price" data-price-display>' + displayPrice + '</span></span>' +
        '</a>' +
        '<div class="wc-card__actions">' +
        variantSelect +
        optSelect +
        cartBtn +
        '<button class="wc-remove-btn" data-remove-wishlist="' + p.id + '">✕ Remove</button>' +
        '</div>' +
        '</div>' +
        '</div>';
    });
    html += '</div>';
    html +=
      '<div class="wc-page-actions">' +
      '<button class="btn wc-btn-clear" id="wc-clear-wishlist">Clear Wishlist</button>' +
      '</div>';

    container.innerHTML = html;

    // Variant select: update price display and ATC variant ID
    container.addEventListener('change', function (e) {
      var sel = e.target.closest('.wc-variant-select');
      if (!sel) return;
      var card = sel.closest('.grid__item');
      var opt = sel.options[sel.selectedIndex];
      var priceEl = card && card.querySelector('[data-price-display]');
      if (priceEl && opt && opt.dataset.price) {
        priceEl.textContent = opt.dataset.price;
      }
      var atcBtn = card && card.querySelector('[data-atc-variant]');
      if (atcBtn) {
        atcBtn.dataset.atcVariant = sel.value;
      }
    });

    container.addEventListener('click', function (e) {
      // Add to cart
      var atcBtn = e.target.closest('[data-atc-variant]');
      if (atcBtn) {
        var card = atcBtn.closest('.grid__item');
        var variantId = parseInt(atcBtn.dataset.atcVariant, 10);
        if (!variantId) {
          // Prompt user to select a base if the dropdown is there
          var variantSel = card && card.querySelector('.wc-variant-select');
          if (variantSel) {
            variantSel.focus();
            variantSel.style.outline = '2px solid #c00';
            setTimeout(function () { variantSel.style.outline = ''; }, 2000);
            return;
          }
          // No variant at all — navigate to product page
          var productLink = card && card.querySelector('.grid-product__image-link');
          if (productLink) { window.location.href = productLink.href; }
          return;
        }
        // Check if a type option is required but not selected
        var typeSelect = card && card.querySelector('.wc-type-select');
        if (typeSelect && !typeSelect.value) {
          typeSelect.focus();
          typeSelect.style.outline = '2px solid #c00';
          setTimeout(function () { typeSelect.style.outline = ''; }, 2000);
          return;
        }
        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding...';
        var cartPayload = { id: variantId, quantity: 1 };
        if (typeSelect && typeSelect.value) {
          cartPayload.properties = { 'Type': typeSelect.value };
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartPayload)
        })
        .then(function (r) { return r.json(); })
        .then(function () {
          atcBtn.textContent = 'Added!';
          setTimeout(function () {
            atcBtn.disabled = false;
            atcBtn.textContent = 'Add to Cart';
          }, 2000);
        })
        .catch(function () {
          atcBtn.textContent = 'Error';
          atcBtn.disabled = false;
        });
        return;
      }

      var removeBtn = e.target.closest('[data-remove-wishlist]');
      if (removeBtn) {
        WC.Wishlist.remove(parseInt(removeBtn.dataset.removeWishlist, 10));
        WC.renderWishlistPage(container);
      }
      if (e.target.id === 'wc-clear-wishlist') {
        if (confirm('Clear your entire wishlist?')) {
          WC.Wishlist.clear();
          WC.renderWishlistPage(container);
        }
      }
    });
  };

  WC.renderComparePage = function (container) {
    var items = WC.Compare.get();

    if (items.length < 2) {
      container.innerHTML =
        '<p class="wc-empty-msg">Add at least 2 products to compare. ' +
        '<a href="/collections/all">Browse products</a></p>';
      return;
    }

    var html = '<div class="wc-compare-table-wrap"><table class="wc-compare-table">';

    // Header row
    html += '<thead><tr><th class="wc-compare-label"></th>';
    items.forEach(function (p) {
      html +=
        '<th>' +
        '<a href="' + p.url + '">' +
        '<img src="' + p.image + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy">' +
        '</a>' +
        '<div class="wc-compare-product-title"><a href="' + p.url + '">' + p.title + '</a></div>' +
        '<button class="wc-remove-btn" data-remove-compare="' + p.id + '">&#x2715; Remove</button>' +
        '</th>';
    });
    html += '</tr></thead><tbody>';

    // Price row
    html += '<tr><td class="wc-compare-label"><strong>Price</strong></td>';
    items.forEach(function (p) {
      html += '<td>' + p.price + '</td>';
    });
    html += '</tr>';

    // Description row
    html += '<tr><td class="wc-compare-label"><strong>Description</strong></td>';
    items.forEach(function (p) {
      html += '<td class="wc-compare-desc">' + (p.description || '&mdash;') + '</td>';
    });
    html += '</tr>';

    // CTA row
    html += '<tr><td class="wc-compare-label"></td>';
    items.forEach(function (p) {
      html +=
        '<td><a href="' + p.url + '" class="btn btn--secondary">View Product</a></td>';
    });
    html += '</tr>';

    html += '</tbody></table></div>';
    html +=
      '<div class="wc-page-actions">' +
      '<button class="btn wc-btn-clear" id="wc-clear-compare">Clear Comparison</button>' +
      '</div>';

    container.innerHTML = html;

    container.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-remove-compare]');
      if (removeBtn) {
        WC.Compare.remove(parseInt(removeBtn.dataset.removeCompare, 10));
        WC.renderComparePage(container);
      }
      if (e.target.id === 'wc-clear-compare') {
        if (confirm('Clear all products from comparison?')) {
          WC.Compare.clear();
          WC.renderComparePage(container);
        }
      }
    });
  };

  // ─── Global click handler ────────────────────────────────────────────────────

  WC.init = function () {
    // Apply theme setting for compare max
    if (window.theme && window.theme.settings && window.theme.settings.compareMaxItems) {
      WC.Compare.max = window.theme.settings.compareMaxItems;
    }

    document.addEventListener('click', function (e) {
      // Wishlist toggle
      var wBtn = e.target.closest('[data-wishlist-btn]');
      if (wBtn) {
        e.preventDefault();
        try {
          var product = JSON.parse(wBtn.dataset.product);
          WC.Wishlist.toggle(product);
        } catch (err) {}
        return;
      }

      // Compare toggle
      var cBtn = e.target.closest('[data-compare-btn]');
      if (cBtn) {
        e.preventDefault();
        try {
          var product = JSON.parse(cBtn.dataset.product);
          WC.Compare.toggle(product);
        } catch (err) {}
        return;
      }

      // Compare bar: remove chip
      var chipRemove = e.target.closest('[data-remove-compare]');
      if (chipRemove) {
        e.preventDefault();
        WC.Compare.remove(parseInt(chipRemove.dataset.removeCompare, 10));
        return;
      }

      // Compare bar: close
      if (e.target.closest('#wc-compare-bar-close')) {
        e.preventDefault();
        var bar = document.getElementById('wc-compare-bar');
        if (bar) bar.classList.remove('wc-compare-bar--visible');
        return;
      }
    });

    WC.Wishlist._updateCounts();
    WC.Wishlist._updateButtons();
    WC.Compare._updateBar();
    WC.Compare._updateButtons();

    var wishlistEl = document.getElementById('wc-wishlist-page');
    if (wishlistEl) WC.renderWishlistPage(wishlistEl);

    var compareEl = document.getElementById('wc-compare-page');
    if (compareEl) WC.renderComparePage(compareEl);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', WC.init.bind(WC));
  } else {
    WC.init();
  }
})();
